import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    Toggle
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Caption,
    Preview,
    Section
} from "../../components/content";
import {
    CheckIcon,
    PlusIcon,
    StarIcon,
    BellIcon
} from "../../anatomy/icons";

const NAME = "Toggle";

const USAGE_CODE = `const [isFavourite, setIsFavourite] = useState(false);

<Toggle
    onPressedChange={setIsFavourite}
    isPressed={isFavourite}
    title="Favourite"
    icon={StarIcon}
/>`;

const VARIANTS = [
    "ghost",
    "outline"
] as const;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const DEMO_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

const DEMO_ROW_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    width: "100%",
    gap: 8
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("togglePage.iconTitle"),
        id: "icon"
    },
    {
        title: t("togglePage.controlTitle"),
        id: "control"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DemoItem = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={DEMO_ITEM_STYLE}
>
    <div
        style={DEMO_ROW_STYLE}
    >
        {children}
    </div>
    <Caption>
        {label}
    </Caption>
</div>;

const TogglePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isFavourite,
        setIsFavourite
    ] = useState(false);

    const [
        isNotified,
        setIsNotified
    ] = useState(true);

    return <Article>
        <ComponentHeader
            lead={t("togglePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("togglePage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("togglePage.overviewCaption")}
            >
                <Toggle
                    title={t("togglePage.favourite")}
                    onPressedChange={setIsFavourite}
                    isPressed={isFavourite}
                    icon={StarIcon}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("togglePage.variantsText")}
            </Paragraph>
            <Preview>
                {
                    VARIANTS.map((variant) => <DemoItem
                        label={variant}
                        key={variant}
                    >
                        <Toggle
                            title={t("togglePage.favourite")}
                            variant={variant}
                            icon={StarIcon}
                        />
                        <Toggle
                            title={t("togglePage.favourite")}
                            isDefaultPressed={true}
                            variant={variant}
                            icon={StarIcon}
                        />
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("togglePage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <Toggle
                        variant="outline"
                        icon={StarIcon}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("togglePage.iconTitle")}
            id="icon"
        >
            <Paragraph>
                {t("togglePage.iconText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="iconDirection"
                >
                    <Toggle
                        title={t("togglePage.favourite")}
                        iconDirection="end"
                        variant="outline"
                        icon={StarIcon}
                    />
                </DemoItem>
                <DemoItem
                    label="isIconOnly"
                >
                    <Toggle
                        title={t("togglePage.notifications")}
                        variant="outline"
                        isIconOnly={true}
                        icon={BellIcon}
                    />
                </DemoItem>
                <DemoItem
                    label="activeIcon"
                >
                    <Toggle
                        title={t("togglePage.follow")}
                        activeIcon={CheckIcon}
                        variant="outline"
                        icon={PlusIcon}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("togglePage.controlTitle")}
            id="control"
        >
            <Paragraph>
                {t("togglePage.controlText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="isDefaultPressed"
                >
                    <Toggle
                        title={t("togglePage.favourite")}
                        isDefaultPressed={true}
                        variant="outline"
                        icon={StarIcon}
                    />
                </DemoItem>
                <DemoItem
                    label={`isPressed={${String(isNotified)}}`}
                >
                    <Toggle
                        title={t("togglePage.notifications")}
                        onPressedChange={setIsNotified}
                        isPressed={isNotified}
                        variant="outline"
                        icon={BellIcon}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("togglePage.statesText")}
            </Paragraph>
            <Preview>
                <Toggle
                    title={t("togglePage.disabled")}
                    variant="outline"
                    isDisabled={true}
                    icon={StarIcon}
                />
                <Toggle
                    title={t("togglePage.disabled")}
                    isDefaultPressed={true}
                    variant="outline"
                    isDisabled={true}
                    icon={StarIcon}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("togglePage.a11yRole"),
                    t("togglePage.a11yKeyboard"),
                    t("togglePage.a11yDisabled"),
                    t("togglePage.a11yIconOnly"),
                    t("togglePage.a11yState")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("togglePage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("togglePage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("togglePage.propActiveIcon"),
                        type: "NCoreUIKitIcon",
                        name: "activeIcon"
                    },
                    {
                        description: t("togglePage.propIsIconOnly"),
                        defaultValue: "false",
                        name: "isIconOnly",
                        type: "boolean"
                    },
                    {
                        description: t("togglePage.propIconDirection"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "iconDirection"
                    },
                    {
                        description: t("togglePage.propVariant"),
                        type: "\"ghost\" | \"outline\"",
                        defaultValue: "\"ghost\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("togglePage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("togglePage.propIsActive"),
                        name: "isPressed",
                        type: "boolean"
                    },
                    {
                        description: t("togglePage.propIsDefaultActive"),
                        name: "isDefaultPressed",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("togglePage.propOnChange"),
                        type: "(isPressed: boolean) => void",
                        name: "onPressedChange"
                    },
                    {
                        description: t("togglePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("togglePage.propIsPressScaleEnabled"),
                        name: "isPressScaleEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("togglePage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("togglePage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TogglePage;
